import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import {
    akvToResource,
    akvToModel,
    parameterizableSecretBaseToModel,
    parameterizablePasswordOrAKVtoSecretBase
} from './azure-key-vault.util';
import { AzureBlobFSLinkedService } from 'app/model/linkedservice/azure-blob-fs.linkedservice';
import { servicePrincipalCredentialTypes } from 'app/model/linkedservice/dynamics.linkedservice';
import { ReferenceAdapter } from '../reference.adapter';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import {
    azureDefaultAuthRefTypes,
    azureBlobFSAuthTypes,
    Credential,
    CredentialTypeOptions,
    ITridentResource,
    ConnectionAuthKind
} from 'shell-models-lib';
import { Constants } from 'app/shared/shared-constants';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { isString } from 'app/shared/utils/string.util';
import { AzureKeyVault } from '../../linkedservice/key-vault';

export const adapts = {
    name: '*',
    properties: {
        description: '*',
        type: '*',
        connectVia: '*',
        typeProperties: {
            url: '*',
            accountKey: 'secret',
            tenant: '*',
            servicePrincipalId: '*',
            servicePrincipalKey: 'secret',
            servicePrincipalCredentialType: '*',
            servicePrincipalCredential: 'secret',
            encryptedCredential: '*',
            azureCloudType: '*',
            credential: '*',
            sasUri: 'secret',
            sasToken: 'secret'
        },
        parameters: '*'
    }
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureBlobFSLinkedService),
    asynchronous: true,
    adapts
})
export class AzureBlobFSLinkedServiceAdapter extends LinkedServiceAdapter<AzureBlobFSLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureBlobFSLinkedService): Promise<void> {
        let finalAuthType: string = model.authenticationMethod;
        if (model.credential) {
            model.authReferenceMethod = azureDefaultAuthRefTypes.CredentialRefTypeAuthentication;

            const credType = model.credential.getType();

            if (credType === CredentialTypeOptions.ServicePrincipal) {
                finalAuthType = azureBlobFSAuthTypes.ServicePrincipalAuthentication;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = azureBlobFSAuthTypes.UserAssignedManagedIdentity;
            } else if (credType === CredentialTypeOptions.UserAuth) {
                finalAuthType = azureBlobFSAuthTypes.UserAuth;
            }
        }
        if (model.isExternalConnection && model.extension._credentialType$ === ConnectionAuthKind.OAuth2) {
            // Trident OAuth2
            finalAuthType = azureBlobFSAuthTypes.UserAuth;
            model.authReferenceMethod = undefined;
        }
        model.authenticationMethod = finalAuthType;
    }

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Partial<Model.AzureBlobFSLinkedService> => {
        const credentialType = tridentResource.credentialDetails.credentialType;
        const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(tridentResource.datasourceReference);
        switch (credentialType) {
            case ConnectionAuthKind.Key:
                return {
                    url: datasourceReference.path,
                    // a fake Account key
                    accountKey: '000'
                };
            case ConnectionAuthKind.SAS:
                return {
                    sasUri: datasourceReference.path
                };
            case ConnectionAuthKind.ServicePrincipal:
                return {
                    url: datasourceReference.path,
                    // a fake ID
                    servicePrincipalId: '000'
                };
            case ConnectionAuthKind.OAuth2:
                return {
                    url: datasourceReference.path
                };
            case ConnectionAuthKind.WorkspaceIdentity:
                return {
                    wiUrl: datasourceReference.path
                }
            default:
                return undefined;
        }
    };

    protected modelConstructor(): new () => AzureBlobFSLinkedService {
        return AzureBlobFSLinkedService;
    }

    protected model(): IModel {
        return AzureBlobFSLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureBlobFSLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureBlobFSLinkedService;

        properties.type = this.model().name;
        const isSP = linkedService.authenticationMethod === azureBlobFSAuthTypes.ServicePrincipalAuthentication;
        const azureCloudType = isSP ? linkedService.azureCloudType : undefined;
        const isInlineSP = isSP && linkedService.authReferenceMethod === azureDefaultAuthRefTypes.InlineRefTypeAuthentication;

        if (linkedService.authenticationMethod === azureBlobFSAuthTypes.SasUriAuthentication) {
            if (linkedService.enableSasUriAKV && linkedService.referedSasUriAKV instanceof AzureKeyVault) {
                properties.typeProperties = {
                    sasUri: akvToResource(linkedService.referedSasUriAKV, this.context)
                };
            } else {
                let sasUri = this.valueToResource(linkedService, AzureBlobFSLinkedService.SAS_URI);
                if (isString(linkedService.sasToken)) {
                    let sasToken = linkedService.sasToken as string;
                    if (sasUri.endsWith('?')) {
                        sasUri = sasUri.substr(0, sasUri.length - 1);
                    }
                    if (sasToken.startsWith('?')) {
                        sasToken = sasToken.substr(1);
                    }
                    properties.typeProperties = {
                        sasUri: sasUri + '?' + sasToken
                    };
                } else {
                    properties.typeProperties = {
                        sasUri,
                        sasToken: parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureBlobFSLinkedService.SAS_TOKEN, this.context)
                    };
                }
            }
        } else {
            properties.typeProperties = {
                url: this.valueToResource(linkedService, AzureBlobFSLinkedService.URL),
                accountKey: linkedService.authenticationMethod === azureBlobFSAuthTypes.AccountKeyAuthentication ?
                    parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureBlobFSLinkedService.ACCOUNT_KEY, this.context) : undefined,
                azureCloudType,
                tenant: isInlineSP ? this.valueToResource(linkedService, AzureBlobFSLinkedService.TENANT) : undefined,
                servicePrincipalId: isInlineSP ? this.valueToResource(linkedService, AzureBlobFSLinkedService.SERVICE_PRINCIPAL_ID) : undefined,
                servicePrincipalKey: linkedService.isOldServicePrincipalResource ?
                    parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureBlobFSLinkedService.SERVICE_PRINCIPAL_KEY, this.context)
                    : undefined,
                servicePrincipalCredentialType: isSP && !linkedService.isOldServicePrincipalResource ?
                    linkedService.servicePrincipalCredentialType : undefined,
                servicePrincipalCredential: isInlineSP && !linkedService.isOldServicePrincipalResource ?
                    (linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert && linkedService.servicePrincipalCert ?
                        akvToResource(linkedService.servicePrincipalCert, this.context) :
                        parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureBlobFSLinkedService.SERVICE_PRINCIPAL_KEY, this.context))
                    : undefined,
                credential: ((isSP && !isInlineSP && linkedService.credential)
                    || ((linkedService.authenticationMethod === azureBlobFSAuthTypes.UserAssignedManagedIdentity || linkedService.authenticationMethod === azureBlobFSAuthTypes.UserAuth)
                        && linkedService.credential)) ?
                    ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference :
                    undefined
            };
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureBlobFSLinkedService): Promise<AzureBlobFSLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureBlobFSLinkedService;
        linkedService.reset();

        if (properties.typeProperties.wiUrl != null) {
            linkedService.authenticationMethod = azureBlobFSAuthTypes.WorkspaceIdentity;
        } else if (properties.typeProperties.sasUri != null) {
            linkedService.authenticationMethod = azureBlobFSAuthTypes.SasUriAuthentication;
            if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.sasUri)) {
                linkedService.enableSasUriAKV = false;
                linkedService.sasUri = Constants.maskedCredential;
                parameterizableSecretBaseToModel(linkedService, AzureBlobFSLinkedService.SAS_TOKEN, properties.typeProperties.sasToken, this.context);
            } else if ((properties.typeProperties.sasUri as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.enableSasUriAKV = true;
                linkedService.referedSasUriAKV = akvToModel(properties.typeProperties.sasUri as Model.AzureKeyVaultSecretReference, this.context);
            } else { // string & parameter
                linkedService.enableSasUriAKV = false;
                ValueAdapter.toModel(linkedService, AzureBlobFSLinkedService.SAS_URI, properties.typeProperties.sasUri);
                parameterizableSecretBaseToModel(linkedService, AzureBlobFSLinkedService.SAS_TOKEN, properties.typeProperties.sasToken, this.context);
            }
        } else {
            ValueAdapter.toModel(linkedService, AzureBlobFSLinkedService.URL, properties.typeProperties.url);
            if (properties.typeProperties.servicePrincipalId) {
                linkedService.authenticationMethod = azureBlobFSAuthTypes.ServicePrincipalAuthentication;
                ValueAdapter.toModel(linkedService, AzureBlobFSLinkedService.SERVICE_PRINCIPAL_ID, properties.typeProperties.servicePrincipalId);
                if (properties.typeProperties.servicePrincipalCredentialType != null) {
                    linkedService.isOldServicePrincipalResource = false;
                    linkedService.servicePrincipalCredentialType = properties.typeProperties.servicePrincipalCredentialType;
                    if (linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalKey) {
                        parameterizableSecretBaseToModel(linkedService, AzureBlobFSLinkedService.SERVICE_PRINCIPAL_KEY, properties.typeProperties.servicePrincipalCredential, this.context);
                    } else if (linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
                        linkedService.servicePrincipalCert = akvToModel(properties.typeProperties.servicePrincipalCredential as Model.SecretBase, this.context);
                    }
                } else {
                    linkedService.isOldServicePrincipalResource = true;
                    parameterizableSecretBaseToModel(linkedService, AzureBlobFSLinkedService.SERVICE_PRINCIPAL_KEY, properties.typeProperties.servicePrincipalKey, this.context);
                }
                ValueAdapter.toModel(linkedService, AzureBlobFSLinkedService.TENANT, properties.typeProperties.tenant);
                linkedService.azureCloudType = properties.typeProperties.azureCloudType;
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
            } else if (properties.typeProperties.encryptedCredential || properties.typeProperties.accountKey) {
                linkedService.authenticationMethod = azureBlobFSAuthTypes.AccountKeyAuthentication;
                parameterizableSecretBaseToModel(linkedService, AzureBlobFSLinkedService.ACCOUNT_KEY, properties.typeProperties.accountKey, this.context);
            } else if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential),
                    properties.typeProperties.credential, this.context);
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.CredentialRefTypeAuthentication;
            } else {
                linkedService.authenticationMethod = azureBlobFSAuthTypes.MSIAuthentication;
                linkedService.authReferenceMethod = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
            }
        }

        return linkedService;
    }
}
